/**
 * WebAuthn/Passkey browser support detection utilities
 */

/**
 * Check if WebAuthn is supported in the current browser
 */
export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function"
  )
}

/**
 * Check if the browser supports platform authenticators (Touch ID, Face ID, Windows Hello)
 * This is more strict than isWebAuthnSupported - it checks for built-in biometric support
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) {
    return false
  }

  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

/**
 * Check if conditional UI (autofill passkeys) is supported
 * This allows passkey suggestions in the browser's autofill UI
 */
export async function isConditionalUISupported(): Promise<boolean> {
  if (!isWebAuthnSupported()) {
    return false
  }

  try {
    if (typeof PublicKeyCredential.isConditionalMediationAvailable === "function") {
      return await PublicKeyCredential.isConditionalMediationAvailable()
    }
    return false
  } catch {
    return false
  }
}

/**
 * Get a human-readable description of WebAuthn support
 */
export async function getWebAuthnSupportInfo(): Promise<{
  supported: boolean
  platformAuthenticator: boolean
  conditionalUI: boolean
  message: string
}> {
  const supported = isWebAuthnSupported()

  if (!supported) {
    return {
      supported: false,
      platformAuthenticator: false,
      conditionalUI: false,
      message: "Your browser doesn't support passkeys",
    }
  }

  const platformAuthenticator = await isPlatformAuthenticatorAvailable()
  const conditionalUI = await isConditionalUISupported()

  if (!platformAuthenticator) {
    return {
      supported: true,
      platformAuthenticator: false,
      conditionalUI,
      message: "Your device doesn't have a built-in authenticator (like Touch ID or Face ID)",
    }
  }

  return {
    supported: true,
    platformAuthenticator: true,
    conditionalUI,
    message: "Passkeys are fully supported on your device",
  }
}

/**
 * Convert a base64url string to Uint8Array
 */
export function base64urlToUint8Array(base64url: string): Uint8Array {
  // Add padding if necessary
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4)
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/") + padding
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Convert Uint8Array to base64url string
 */
export function uint8ArrayToBase64url(bytes: Uint8Array): string {
  let binary = ""
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "")
}
